import React, { useState } from 'react';
import { ErrorBoundary as ReactErrorBoundary } from 'react-error-boundary';
import { captureError } from '../lib/observability';

function ErrorFallback({ error, resetErrorBoundary }) {
  const [showDetails, setShowDetails] = useState(false);

  return (
    <div
      role="alert"
      style={{
        minHeight: '100dvh',
        backgroundColor: '#0A0A0F',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        fontFamily: '"Inter", sans-serif',
      }}
    >
      <div
        style={{
          maxWidth: '360px',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '20px',
        }}
      >
        {/* Error icon */}
        <div
          style={{
            width: '64px',
            height: '64px',
            borderRadius: '50%',
            backgroundColor: 'rgba(239,68,68,0.12)',
            border: '1px solid rgba(239,68,68,0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#EF4444"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </div>

        {/* Heading */}
        <div style={{ textAlign: 'center' }}>
          <h1
            style={{
              color: '#FFFFFF',
              fontSize: '20px',
              fontWeight: '600',
              margin: '0 0 8px 0',
              letterSpacing: '-0.01em',
            }}
          >
            Something went wrong
          </h1>
          <p
            style={{
              color: '#64748B',
              fontSize: '14px',
              margin: 0,
              lineHeight: '1.5',
            }}
          >
            An unexpected error occurred. Try reloading the app.
          </p>
        </div>

        {/* Reload button */}
        <button
          onClick={resetErrorBoundary}
          style={{
            background: 'linear-gradient(135deg, #00E5CC, #00B4D8)',
            border: 'none',
            borderRadius: '12px',
            color: '#0A0A0F',
            cursor: 'pointer',
            fontFamily: '"Inter", sans-serif',
            fontSize: '14px',
            fontWeight: '600',
            letterSpacing: '0.05em',
            padding: '12px 32px',
            textTransform: 'uppercase',
            boxShadow: '0 0 20px rgba(0, 229, 204, 0.3)',
            width: '100%',
          }}
        >
          Reload App
        </button>

        {/* Error details (collapsed by default) */}
        <div style={{ width: '100%' }}>
          <button
            onClick={() => setShowDetails((v) => !v)}
            style={{
              background: 'none',
              border: 'none',
              color: '#64748B',
              cursor: 'pointer',
              fontFamily: '"Inter", sans-serif',
              fontSize: '12px',
              padding: '0',
              textDecoration: 'underline',
            }}
            aria-expanded={showDetails}
          >
            {showDetails ? 'Hide' : 'Show'} error details
          </button>
          {showDetails && (
            <pre
              style={{
                marginTop: '12px',
                padding: '12px',
                backgroundColor: 'rgba(239,68,68,0.06)',
                border: '1px solid rgba(239,68,68,0.15)',
                borderRadius: '8px',
                color: '#EF4444',
                fontSize: '11px',
                overflowX: 'auto',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontFamily: '"JetBrains Mono", monospace',
              }}
            >
              {error?.message}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
}

function onError(error, info) {
  console.error('[ErrorBoundary] Uncaught error:', error, info);
  captureError(error, { componentStack: info?.componentStack });
}

export default function ErrorBoundary({ children }) {
  return (
    <ReactErrorBoundary
      FallbackComponent={ErrorFallback}
      onError={onError}
      onReset={() => window.location.reload()}
    >
      {children}
    </ReactErrorBoundary>
  );
}
